<template>
  <div
    v-if="FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO"
    class="mb-12 flex flex-wrap gap-8"
  >
    <div>Content cards: {{ contentCards.length }}</div>
    <div>cards: {{ cards.length }}</div>

    <div>isRequestingContentCards (Braze): {{ isRequestingContentCards }}</div>
    <div>brazeUnavailable: {{ brazeUnavailable }}</div>
    <div>
      currentContentCard.props: {{ currentContentCard?.component?.props }}
    </div>
    <div>currentContentCard.tag: {{ currentContentCard?.component?.tag }}</div>
    <div>activeTabIndex: {{ activeTabIndex }}</div>
    <div>showFallback {{ showFallback }}</div>
    <div>fallbackCards: {{ fallbackCards }}</div>
    <div>firstTabStatus: {{ firstTabStatus }}</div>
    <div>isFirstTabContentResolved: {{ isFirstTabContentResolved }}</div>
  </div>
  <Background
    :background
    :class="{
      'p-16 md:p-24 rounded-lg': !!background,
    }"
    container-class="!items-start"
  >
    <RevSkeleton
      v-if="forceSingleContent && !isFirstTabContentResolved"
      class="mb-16"
      height="32px"
      shape="rectangle"
      width="206px"
    />
    <BlockTitle v-else :title="mainTitle" />

    <div
      v-if="!forceSingleContent"
      ref="tabs-scroller"
      class="mb-20 mt-4 flex grow flex-col justify-between overflow-auto scrollbar-none max-w-full"
      :class="maskClass"
    >
      <div
        :aria-label="title || ''"
        class="flex w-max items-start gap-8 overflow-x-auto overflow-y-hidden scrollbar-none max-w-full"
        role="tablist"
      >
        <template v-if="showTabsSkeleton">
          <RevSkeleton height="40px" shape="rectangle" width="130px" />
          <RevSkeleton height="40px" shape="rectangle" width="130px" />
        </template>

        <RevChip
          v-for="(tab, tabIndex) in displayedContentCardTabs"
          v-else
          :id="`tab-${tabIndex}`"
          :key="tab.id"
          ref="tabItem"
          aria-controls="content-card-panel"
          :aria-selected="activeTabIndex === tabIndex"
          class="shrink-0"
          :data-test="`tab-${tabIndex}`"
          :end-icon="tab.icon"
          :label="tab.title"
          :model-value="activeTabIndex === tabIndex"
          role="tab"
          :tabindex="activeTabIndex === tabIndex ? 0 : -1"
          @click="() => onTabClick(tabIndex)"
          @keydown.left.prevent="goToPreviousTab"
          @keydown.right.prevent="goToNextTab"
        />
      </div>
    </div>

    <div
      id="content-card-panel"
      ref="content-card-panel"
      :aria-labelledby="
        forceSingleContent ? undefined : `tab-${activeTabIndex}`
      "
      class="h-[408px] md:h-[394px] w-full"
      data-test="content-card-panel"
      :role="forceSingleContent ? undefined : 'tabpanel'"
    >
      <CardCollectionSkeleton
        v-if="isRequestingContentCards"
        layout="carousel"
      />
      <component
        :is="currentContentCard?.component?.tag || null"
        v-show="!isRequestingContentCards"
        :key="currentContentCardCarouselKey"
        v-bind="currentContentCard?.component?.props"
        @error="onCardError"
        @loaded="onCardLoaded"
      />

      <ErrorState v-if="showErrorState" @retry="handleRetry" />
    </div>
  </Background>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import type { PersonalizedFeedApiProps } from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevChip } from '@ds/components/Chip'
import { RevSkeleton } from '@ds/components/Skeleton'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import Background from '../../shared-components/Background/Background.vue'
import BlockTitle from '../../shared-components/BlockTitle/BlockTitle.vue'
import CardCollectionSkeleton from '../../shared-components/CardCollectionSkeleton/CardCollectionSkeleton.vue'
import { ON_RETRY_INJECTION_KEY } from '../RefinedProducts/retry/on-retry.injection-key'
import type { RetryFunction } from '../RefinedProducts/retry/retry-function'

import {
  transformContentCards,
  transformFallbackBlocks,
} from './PersonalizedFeed.utils'
import ErrorState from './components/ErrorState.vue'
import { useBrazeCards } from './composables/useBrazeCards'
import { useFallbackError } from './composables/useFallbackError'
import { useIsVisible } from './composables/useIsVisible'
import { useActiveTabIndex } from './composables/useTabIndex'

export type PersonalizedFeedBlockProps = PersonalizedFeedApiProps & BlockProps

const {
  feed,
  fallback,
  blockPosition,
  title,
  forceSingleContent,
  productQuery,
  tracking,
} = defineProps<PersonalizedFeedBlockProps>()

const { FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO = false } =
  useRuntimeConfig().public

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error', 'reset-error', 'loaded'])

const tabItem = useTemplateRef('tabItem')
const scrollerRef = useTemplateRef<HTMLDivElement>('tabs-scroller')
const { maskClass } = useScrollMaskClass(scrollerRef)

const brazeUnavailable = ref(false)

const { cards, fetchCards, isRequestingContentCards, waitForBrazeToBeReady } =
  useBrazeCards()

const contentCards = computed(() =>
  transformContentCards({
    cards: cards.value,
    feed,
    productQuery,
  }),
)

const fallbackCards = computed(() =>
  transformFallbackBlocks({
    fallback,
    blockPosition,
    title,
  }),
)

const {
  activeTabIndex,
  setActiveTabIndex,
  goToPreviousTab,
  goToNextTab,
  onTabChange,
} = useActiveTabIndex(contentCards)

const {
  showFallback,
  showErrorState,
  resetFallbackErrorState,
  onCardError,
  firstTabStatus,
  onCardLoaded,
} = useFallbackError({
  activeTabIndex,
  brazeUnavailable,
  fallbackCards,
})

const { trackBlockImpression, trackClick } = useTracking()

onTabChange(() => {
  resetFallbackErrorState()
})

const contentCardPanelRef = useTemplateRef('content-card-panel')

const hydrationCompleted = ref(false)

const currentContentCard = computed(() => {
  if (!hydrationCompleted.value) {
    return null
  }

  if (brazeUnavailable.value) {
    return {
      source: 'fallback',
      component: fallbackCards.value[activeTabIndex.value],
    }
  }

  if (contentCards.value.length === 0) {
    return {
      source: 'fallback',
      component: fallbackCards.value[activeTabIndex.value],
    }
  }

  if (showFallback.value) {
    return {
      source: 'fallback',
      component: fallbackCards.value[activeTabIndex.value],
    }
  }

  return {
    source: 'content-card',
    component: contentCards.value[activeTabIndex.value],
  }
})

const currentContentCardCarouselKey = computed(
  () =>
    `${currentContentCard.value?.component?.id}-${showFallback.value ? 'fallback' : ''}`,
)

const isFirstTabContentResolved = computed(() => {
  if (brazeUnavailable.value) {
    return true
  }

  if (isRequestingContentCards.value) {
    return false
  }

  if (contentCards.value.length === 0) {
    return true
  }

  return firstTabStatus.value !== 'pending'
})

const showTabsSkeleton = computed(
  () => !forceSingleContent && !isFirstTabContentResolved.value,
)

const displayedContentCardTabs = computed(() => {
  if (brazeUnavailable.value) {
    return fallbackCards.value
  }

  if (contentCards.value.length === 0) {
    return fallbackCards.value
  }

  // If the first tab's content card failed, we will display the fallback and reflect that in the tab's name
  if (firstTabStatus.value === 'error') {
    return [fallbackCards.value[0], ...contentCards.value.slice(1)]
  }

  return contentCards.value
})

const mainTitle = computed(() => {
  // if forceSingleContent, we display the title of the first card (if it exists)
  if (forceSingleContent) {
    return displayedContentCardTabs.value?.[0]?.title || title
  }

  return title
})

const { isBrazeUserConsentGiven } = useDataConsent()

let lastCalledWith: boolean | undefined
function setInitialState(canUseBraze: boolean) {
  // this avoids to re-execute the core of this function in some cases
  // initially, we might have waitForBrazeToBeReady which returns {isEnabled: true}
  // just after, isBrazeUserConsentGiven might be updated to true and trigger this function again
  if (canUseBraze === lastCalledWith) return

  lastCalledWith = canUseBraze

  activeTabIndex.value = 0
  if (canUseBraze) {
    fetchCards()
    brazeUnavailable.value = false
  } else {
    brazeUnavailable.value = true
  }
}

onMounted(async () => {
  hydrationCompleted.value = true

  const { isEnabled } = await waitForBrazeToBeReady()

  setInitialState(isEnabled)
})

watch(
  () => isBrazeUserConsentGiven.value,
  (consent) => {
    setInitialState(consent)
  },
)

let retryFunctions = Array<RetryFunction>()

function useRegisterRetryFn(fn: RetryFunction) {
  retryFunctions.push(fn)

  onUnmounted(() => {
    retryFunctions = retryFunctions.filter((f) => f !== fn)
  })
}

provide(ON_RETRY_INJECTION_KEY, useRegisterRetryFn)

function onTabClick(i: number) {
  setActiveTabIndex(i)
  requestAnimationFrame(() => {
    const targetTab = tabItem.value?.find(
      (el) => el?.$attrs.id === `tab-${i}`,
    )?.$el
    if (targetTab) {
      targetTab.focus()
      targetTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  })
  trackClick({
    ...tracking,
    position: i + 1,
    blockTitle: title || '',
    tabName: displayedContentCardTabs.value[i]?.title,
    source: currentContentCard.value?.source || '',
  })
}

function handleRetry() {
  if (activeTabIndex.value === 0) {
    firstTabStatus.value = 'pending'
  }
  retryFunctions.forEach((fn) => fn?.())
}

// send an block_impression event when the whole block enters the viewport and the first tab has been loaded
function trackPersonalizedFeedImpressionWithFirstTabInfo() {
  trackBlockImpression({
    ...tracking,
    blockTitle: title || '',
    tabs: displayedContentCardTabs.value.map((tab) => tab.title),
    source: currentContentCard.value?.source || '',
    tabName: displayedContentCardTabs.value[activeTabIndex.value]?.title,
  })
}

const { isVisible } = useIsVisible({
  targetElement: contentCardPanelRef,
  onIntersection: ({ visible }) => {
    if (visible && isFirstTabContentResolved.value) {
      trackPersonalizedFeedImpressionWithFirstTabInfo()
    }
  },
})

watch(isFirstTabContentResolved, (resolved) => {
  if (resolved && isVisible.value) {
    trackPersonalizedFeedImpressionWithFirstTabInfo()
  }
})
</script>
